import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Code2, Database, Server, Globe, Shield, Zap, Award, Cpu, GitBranch } from 'lucide-react';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { y: 60, opacity: 0, rotateX: -45 },
    visible: {
      y: 0,
      opacity: 1,
      rotateX: 0,
      transition: {
        duration: 0.8,
        ease: [0.175, 0.885, 0.32, 1.275]
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0, rotateY: -90 },
    visible: {
      scale: 1,
      opacity: 1,
      rotateY: 0,
      transition: {
        duration: 0.8,
        ease: [0.175, 0.885, 0.32, 1.275]
      }
    }
  };

  const expertise = [
    "Full-Stack Development",
    "Go (Gin) Backend",
    "REST APIs",
    "Docker Deployment",
    "Proxmox & Cloud-init Automation",
    "Nginx Server Setup",
    "MySQL & Redis Management",
    "Cloudflare & SSL Security",
    "CI/CD (Shell, Docker Compose)",
    "Performance Tuning",
    "OAuth & JWT Authentication",
    "Offline-First Systems",
    "Git & Swagger Documentation"
  ];

  const achievements = [
    { icon: Code2, label: "Full-Stack", value: "Go + React" },
    { icon: Server, label: "DevOps", value: "Docker & CI/CD" },
    { icon: Database, label: "Databases", value: "MySQL & Redis" },
    { icon: Shield, label: "Security", value: "OAuth & SSL" },
  ];

  return (
    <section id="about" className="about section">
      <div className="about-background">
        <div className="about-particles"></div>
      </div>
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 
            className="section-title"
            variants={itemVariants}
            whileHover={{
              scale: 1.05,
              rotateX: 5,
              transition: { duration: 0.3 }
            }}
          >
            About Me
          </motion.h2>
          
          <div className="about-content">
            <motion.div 
              className="about-text"
              variants={itemVariants}
            >
              <motion.div 
                className="about-intro"
                whileHover={{
                  scale: 1.02,
                  y: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <h3>Full-Stack Developer & DevOps Engineer</h3>
                <p className="intro-highlight">
                  Building scalable, reliable, and secure applications from backend APIs to production deployment
                </p>
              </motion.div>
              
              <motion.div 
                className="about-description"
                variants={itemVariants}
              >
                <p>
                  I'm <strong>Rishvanth</strong>, a <strong>Full-Stack Developer</strong> and <strong>DevOps Engineer</strong> passionate 
                  about building scalable, reliable, and secure applications. I specialize in <strong>Go (Golang)</strong>, 
                  <strong>React</strong>, and <strong>Docker-based infrastructures</strong> — managing everything from backend APIs 
                  to production deployment.
                </p>
                <p>
                  I've developed platforms like <strong>PS Portal</strong> and <strong>Learnathon</strong>, focusing on 
                  <strong>clean architecture</strong>, <strong>performance optimization</strong>, and <strong>automation</strong>. 
                  My expertise spans full-stack development, server management, CI/CD pipelines, and security implementations.
                </p>
              </motion.div>

              {/* Achievement Grid */}
              <motion.div 
                className="achievements-grid"
                variants={itemVariants}
              >
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    className="achievement-card"
                    variants={cardVariants}
                    whileHover={{
                      scale: 1.05,
                      rotateY: 10,
                      z: 20,
                      transition: { duration: 0.3 }
                    }}
                    custom={index}
                  >
                    <div className="achievement-icon">
                      <achievement.icon size={24} />
                    </div>
                    <div className="achievement-content">
                      <h4>{achievement.label}</h4>
                      <p>{achievement.value}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="about-image"
              variants={itemVariants}
            >
              <motion.div 
                className="profile-card"
                variants={cardVariants}
                whileHover={{ 
                  rotateY: 15,
                  rotateX: 5,
                  translateY: -15,
                  scale: 1.05,
                  transition: { duration: 0.4 }
                }}
              >
                <div className="profile-glow"></div>
                <div className="profile-avatar">
                  <span className="avatar-text">R</span>
                  <div className="avatar-ring"></div>
                </div>
                <div className="profile-info">
                  <h3 className="profile-name">Rishvanth</h3>
                  <p className="profile-role">Full-Stack Developer & DevOps Engineer</p>
                  <div className="profile-tech">
                    <span className="tech-badge">Go</span>
                    <span className="tech-badge">React</span>
                    <span className="tech-badge">Docker</span>
                  </div>
                </div>
                <div className="profile-stats">
                  <div className="stat">
                    <span className="stat-number">10+</span>
                    <span className="stat-label">Projects</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">5+</span>
                    <span className="stat-label">Technologies</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">100%</span>
                    <span className="stat-label">Dedication</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Expertise Section */}
          <motion.div
            className="expertise-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="section-subtitle">Areas of Expertise</h3>
            <div className="expertise-grid">
              {[
                {
                  title: "Full-Stack Development",
                  description: "Building scalable web applications with modern frameworks and best practices",
                  icon: "💻"
                },
                {
                  title: "DevOps & Cloud",
                  description: "Automating deployments and managing cloud infrastructure",
                  icon: "☁️"
                },
                {
                  title: "Database Design",
                  description: "Designing and optimizing database schemas for performance",
                  icon: "🗄️"
                },
                {
                  title: "API Development",
                  description: "Creating robust and scalable RESTful APIs",
                  icon: "🔌"
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="expertise-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="expertise-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
